import { useEffect } from "react"
import { isUndefinedOrNull } from "../utils/utils"
import { classes } from "./react"

interface SelectProps<T = string | number> {
    label?: React.ReactNode
    options: (readonly [value: string | number, text: string])[]
    value: T
    setValue(value: T): void
    defaultLabel?: string
    disabled?: boolean
    className?: string
    /**
     * Emulates a placeholder for the select input by using `<option disabled hidden>` (Use as an alternative to `defaultLabel`, which is still a selectable option)
     */
    placeholder?: string
    style?: React.CSSProperties
    title?: string
    autoComplete?: string
}

/** Dropdown component. NOTE: do NOT use for selecting options where the value is number - use SelectNumber instead for the dropdown to work correctly. */
export function Select<T extends string | number>({
    options,
    value,
    setValue,
    defaultLabel,
    disabled,
    className,
    placeholder,
    style,
    title,
    autoComplete,
}: SelectProps<T>) {
    return (
        <select
            className={classes("form-control", className)}
            style={style}
            value={value}
            onChange={(e) => setValue(e.target.value as unknown as T)}
            disabled={disabled}
            title={title}
            autoComplete={autoComplete}
        >
            {placeholder ? (
                <option disabled hidden value="">
                    {placeholder}
                </option>
            ) : null}
            {!isUndefinedOrNull(defaultLabel) && <option value="">{defaultLabel}</option>}
            {options?.map((s) => (
                <option key={s[0]} value={s[0]}>
                    {s[1]}
                </option>
            ))}
        </select>
    )
}

export function SelectNumber({ value, setValue, ...props }: SelectProps<number>) {
    return (
        <Select
            {...{
                ...props,
                value: value ? String(value) : "",
                setValue: (v: string) => setValue(v ? Number(v) : undefined),
            }}
        />
    )
}

type FormGroupSelectProps<T = string | number> = React.PropsWithChildren<
    SelectProps<T> & {
        label?: React.ReactNode
        inputClassName?: string
        invalid?: boolean | string
    }
>

/** Form group with a dropdown. NOTE: do NOT use for options where the value is a number - use FormGroupSelectNumber for the component to work correctly. */
export function FormGroupSelect<T extends string | number>({
    className,
    label,
    children,
    invalid,
    inputClassName,
    ...selectProps
}: FormGroupSelectProps<T>) {
    return (
        <div className={classes("form-group", className, invalid && "invalid")}>
            {label && <label>{label}</label>}
            <Select {...{ ...selectProps, className: inputClassName }} />
            {children}
        </div>
    )
}

export function FormGroupSelectNumber({
    label,
    options,
    value,
    setValue,
    defaultLabel,
    className,
    inputClassName,
    disabled,
    children,
}: FormGroupSelectProps<number>) {
    return (
        <FormGroupSelect
            {...{
                label,
                defaultLabel,
                options,
                className,
                inputClassName,
                value: value ? String(value) : "",
                setValue: (v: string) => setValue(v ? Number(v) : undefined),
                disabled,
                children,
            }}
        />
    )
}
